import React, { useCallback, useRef } from "react"
import { MessageComposer } from "@mallardbay/lib-react-components"

import { AI_CHAT_ATTACHMENT_FILE_ACCEPT } from "~components/shared/chat/chat-file-attachment-helpers"
import useChatInputState from "~components/shared/chat/use-chat-input-state"
import COPY from "~config/copy-constants"
import { TEST_IDS } from "~config/constants"
import type useAiChat from "~utils/hooks/use-ai-chat"

interface Props {
    readonly sendMessage: ReturnType<typeof useAiChat>["sendMessage"]
    readonly isLoading: boolean
    readonly placeholder?: string
}

export default function ChatInput({
    sendMessage,
    isLoading,
    placeholder = COPY.AI_CHAT_PLACEHOLDER,
}: Props) {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const {
        message,
        setMessage,
        pendingAttachments,
        canSubmit,
        uploadingCount,
        handleSubmit,
        handlePastedFiles,
        handleRemoveAttachment,
    } = useChatInputState({ sendMessage, isLoading })
    const handlePaste = useCallback(
        (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
            const files = Array.from(event.clipboardData.files)
            if (!files.length) return

            event.preventDefault()
            void handlePastedFiles(files)
        },
        [handlePastedFiles]
    )
    const handleAttachClick = useCallback(() => {
        fileInputRef.current?.click()
    }, [])
    const handleFileInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(event.target.files ?? [])
            event.target.value = ""
            if (!files.length) return

            void handlePastedFiles(files)
        },
        [handlePastedFiles]
    )

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={AI_CHAT_ATTACHMENT_FILE_ACCEPT}
                data-testid={TEST_IDS.CHAT_INPUT_FILE_INPUT}
                onChange={handleFileInputChange}
            />
            <MessageComposer
                value={message}
                onChange={setMessage}
                onSubmit={handleSubmit}
                attachments={pendingAttachments}
                uploadingCount={uploadingCount}
                onAttachClick={handleAttachClick}
                onPaste={handlePaste}
                onRemoveAttachment={handleRemoveAttachment}
                placeholder={placeholder}
                isSubmitting={isLoading}
                canSubmit={canSubmit}
                attachAriaLabel={COPY.AI_CHAT_ATTACH_ARIA}
                submitAriaLabel={COPY.SUBMIT}
                removeAttachmentAriaLabel={COPY.AI_CHAT_REMOVE_ATTACHMENT_ARIA}
                testIds={{
                    attachButton: TEST_IDS.CHAT_INPUT_ATTACH_BUTTON,
                    submitButton: TEST_IDS.CHAT_INPUT_SUBMIT_BUTTON,
                    attachment: TEST_IDS.CHAT_INPUT_ATTACHMENT,
                    uploadingAttachment:
                        TEST_IDS.CHAT_INPUT_ATTACHMENT_UPLOADING,
                    removeAttachmentButton:
                        TEST_IDS.CHAT_INPUT_REMOVE_ATTACHMENT_BUTTON,
                    fileIcon: TEST_IDS.CHAT_ATTACHMENT_FILE_ICON,
                }}
            />
        </>
    )
}
